import { useState, useEffect, useCallback, useRef } from "react";
import { Box, Tabs, Text, Stack, Loader, Center, Paper, Badge, Pagination } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import InterestCard from "@/pages/interests/components/InterestCard";
import TitleComponent from "@/components/shared/TitleComponent";
import { useUserContext } from "@/context/UserContext";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  navTabsListStyles,
} from "@/shared/styles/navTabsLayout";

export default function InterestsPage() {
  const navigate = useNavigate();
  const { refreshPendingInterestCount } = useUserContext();
  const [activeTab, setActiveTab] = useState<string | null>("incoming");

  // ── incoming ────────────────────────────────────────────────────────────────
  const [incomingRequests, setIncomingRequests] = useState<any[]>([]);
  const [incomingPage, setIncomingPage] = useState(1);
  const [incomingTotalPages, setIncomingTotalPages] = useState(1);
  const [incomingTotalCount, setIncomingTotalCount] = useState(0);
  const [loadingIncoming, setLoadingIncoming] = useState(true);

  // ── sent ────────────────────────────────────────────────────────────────────
  const [sentRequests, setSentRequests] = useState<any[]>([]);
  const [sentPage, setSentPage] = useState(1);
  const [sentTotalPages, setSentTotalPages] = useState(1);
  const [sentTotalCount, setSentTotalCount] = useState(0);
  const [loadingSent, setLoadingSent] = useState(true);

  // ── shared ──────────────────────────────────────────────────────────────────
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // ── fetchers with useCallback for stable references ────────────────────────
  const fetchIncoming = useCallback(async () => {
    setLoadingIncoming(true);
    try {
      const response = await trpc.collaboration.getReceived.query({ page: incomingPage, limit: 10 });
      const data = Array.isArray(response) ? response : response?.matches || [];
      const pagination = !Array.isArray(response) ? response?.pagination : null;
      setIncomingRequests(data);
      if (pagination) {
        setIncomingTotalPages(pagination.totalPages);
        setIncomingTotalCount(pagination.total);
      }
    } catch (err) {
      handleTrpcError(err);
      setIncomingRequests([]);
    } finally {
      setLoadingIncoming(false);
    }
  }, [incomingPage]);

  const fetchSent = useCallback(async () => {
    setLoadingSent(true);
    try {
      const response = await trpc.collaboration.getSent.query({ page: sentPage, limit: 10 });
      const data = Array.isArray(response) ? response : response?.matches || [];
      const pagination = !Array.isArray(response) ? response?.pagination : null;
      setSentRequests(data);
      if (pagination) {
        setSentTotalPages(pagination.totalPages);
        setSentTotalCount(pagination.total);
      }
    } catch (err) {
      handleTrpcError(err);
      setSentRequests([]);
    } finally {
      setLoadingSent(false);
    }
  }, [sentPage]);
  // ── ref to track initial mount ─────────────────────────────────────────────
  const isInitialMount = useRef(true);

  // ── smart effect: fetch both on initial load, then only active tab ──────────
  useEffect(() => {
    if (isInitialMount.current) {
      // First load: fetch both datasets to populate badge counts immediately
      fetchIncoming();
      fetchSent();
      isInitialMount.current = false;
    } else {
      // Subsequent renders: fetch only the active tab's data
      if (activeTab === "incoming") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        fetchIncoming();
      } else if (activeTab === "sent") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        fetchSent();
      }
    }
  }, [activeTab, fetchIncoming, fetchSent]);

  // ── action handlers (unchanged) ─────────────────────────────────────────────
  const handleAccept = async (matchId: string) => {
    setActionLoading(matchId);
    try {
      await trpc.collaboration.accept.mutate({ matchId });
      successNotification({ title: "Success", message: "Collaboration request accepted!" });
      fetchIncoming();
      refreshPendingInterestCount();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (matchId: string) => {
    setActionLoading(matchId);
    try {
      await trpc.collaboration.reject.mutate({ matchId });
      successNotification({ title: "Success", message: "Collaboration request declined." });
      fetchIncoming();
      refreshPendingInterestCount();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMakePayment = (matchId: string) => {
    const request = [...incomingRequests, ...sentRequests].find((r) => r.id === matchId);
    navigate(`/requests/match-success/${matchId}`, { state: { request } });
  };

  const handleViewProfile = (companyId: string) => navigate(`/requests/${companyId}`);
  const handleOpenWorkspace = (matchId: string) => navigate(`/collaborations/${matchId}`);

  // ── shared card props builder ───────────────────────────────────────────────
  const cardProps = (request: any, isRequester: boolean) => ({
    matchId: request.id,
    company: isRequester ? request.receiver : request.requester,
    collaborationStatus: request.status,
    isRequester,
    requesterPaid: request.requesterPaid,
    receiverPaid: request.receiverPaid,
    requesterSignedNDA: request.requesterSignedNDA,
    receiverSignedNDA: request.receiverSignedNDA,
    paymentDeadline: request.paymentDeadline,
    receivedAt: request.createdAt,
    onAccept: handleAccept,
    onDecline: handleDecline,
    onMakePayment: handleMakePayment,
    onViewProfile: handleViewProfile,
    onOpenWorkspace: handleOpenWorkspace,
    isLoading: actionLoading === request.id,
  });

  // ── reusable panel content ──────────────────────────────────────────────────
  const renderPanel = ({
    loading,
    requests,
    isRequester,
    totalPages,
    page,
    setPage,
    emptyLabel,
  }: {
    loading: boolean;
    requests: any[];
    isRequester: boolean;
    totalPages: number;
    page: number;
    setPage: (p: number) => void;
    emptyLabel: string;
  }) => (
    <>
      {loading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : (
        <Stack gap="md">
          {requests.length === 0 ? (
            <Paper
              p="xl"
              radius="lg"
              withBorder
              style={{
                borderColor: "#04305c",
                backgroundColor: "#ebf0ff",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
              }}
            >
              <Text c="gray.5" ta="center">
                {emptyLabel}
              </Text>
            </Paper>
          ) : (
            requests.map((request) => <InterestCard key={request.id} {...cardProps(request, isRequester)} />)
          )}

          {totalPages > 1 && (
            <Center mt="xl">
              <Pagination total={totalPages} value={page} onChange={setPage} size="md" radius="md" />
            </Center>
          )}
        </Stack>
      )}
    </>
  );

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <Box mih="100vh" pb={20}>
      <TitleComponent title="Requests" description="Manage incoming and outgoing collaboration requests" />

      <Tabs value={activeTab} onChange={setActiveTab} p="md">
        <Box w="100%" style={NAV_TABS_FULL_WIDTH_LINE_STYLE}>
          <Tabs.List styles={navTabsListStyles(2)}>
          <Tabs.Tab
            value="incoming"
            px={{ base: "md", sm: "lg" }}
            py="xs"
            fz={{ base: "xs", sm: "sm" }}
            style={{
              ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
              backgroundColor: activeTab === "incoming" ? "#04305c" : "#ebf0ff",
              color: activeTab === "incoming" ? "white" : "var(--mantine-color-slate-6)",
              borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
              border: activeTab === "incoming" ? "1px solid #04305c" : "1px solid #E5E7EB",
              borderBottom: "none",
              whiteSpace: "nowrap",
              fontWeight: activeTab === "incoming" ? 500 : 400,
              cursor: "pointer",
              minHeight: 44,
            }}
            rightSection={
              <Box
                w={28}
                h={28}
                miw={28}
                mih={28}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              >
                <Badge
                  w={24}
                  h={24}
                  p={0}
                  radius="xl"
                  variant="filled"
                  styles={{
                    root: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 24,
                      backgroundColor: activeTab === "incoming" ? "#ffffff" : "#94a3b8",
                      color: activeTab === "incoming" ? "#04305c" : "#ffffff",
                      fontWeight: 600,
                      fontSize: 11,
                      border: "none",
                    },
                  }}
                >
                  {incomingTotalCount}
                </Badge>
              </Box>
            }
          >
            Received Requests
          </Tabs.Tab>

          <Tabs.Tab
            value="sent"
            px={{ base: "md", sm: "lg" }}
            py="xs"
            fz={{ base: "xs", sm: "sm" }}
            style={{
              ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
              backgroundColor: activeTab === "sent" ? "#04305c" : "#ebf0ff",
              color: activeTab === "sent" ? "white" : "var(--mantine-color-slate-6)",
              borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
              border: activeTab === "sent" ? "1px solid #04305c" : "1px solid #E5E7EB",
              borderBottom: "none",
              whiteSpace: "nowrap",
              fontWeight: activeTab === "sent" ? 500 : 400,
              cursor: "pointer",
              minHeight: 44,
            }}
            rightSection={
              <Box
                w={28}
                h={28}
                miw={28}
                mih={28}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              >
                <Badge
                  w={24}
                  h={24}
                  p={0}
                  radius="xl"
                  variant="filled"
                  styles={{
                    root: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 24,
                      backgroundColor: activeTab === "sent" ? "#ffffff" : "#94a3b8",
                      color: activeTab === "sent" ? "#04305c" : "#ffffff",
                      fontWeight: 600,
                      fontSize: 11,
                      border: "none",
                    },
                  }}
                >
                  {sentTotalCount}
                </Badge>
              </Box>
            }
          >
            Sent Requests
          </Tabs.Tab>
          </Tabs.List>
        </Box>

        <Tabs.Panel value="incoming" pt="xl">
          {renderPanel({
            loading: loadingIncoming,
            requests: incomingRequests,
            isRequester: false,
            totalPages: incomingTotalPages,
            page: incomingPage,
            setPage: setIncomingPage,
            emptyLabel: "No incoming requests",
          })}
        </Tabs.Panel>

        <Tabs.Panel value="sent" pt="xl">
          {renderPanel({
            loading: loadingSent,
            requests: sentRequests,
            isRequester: true,
            totalPages: sentTotalPages,
            page: sentPage,
            setPage: setSentPage,
            emptyLabel: "No sent requests",
          })}
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}
